import { useEffect, useState } from "react";
import { Download, ShieldOff } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { fecha } from "@/lib/format";
import { cuantos, descargarBlob, errorDeBlob, lineaAcceso } from "@/lib/privacidad";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

// Estado de una categoría en la ficha: «pendiente» es lo que ha llegado después de la última supresión.
const estado = (iso, pendiente) => {
  if (!iso) return "guardadas";
  return `suprimidas el ${fecha(iso)}${pendiente ? "; las que han llegado después siguen guardadas" : ""}`;
};

const CATEGORIAS = [
  ["documentos", "Fotos del DNI y del carné (también del conductor adicional) y ubicación de la recogida y la devolución autónomas"],
  ["vehiculo", "Fotos del estado del vehículo (entrega, daños, devolución e inspección)"],
];

// Datos personales de una reserva: suprimir fotos a petición del cliente, exportar sus datos y ver quién del panel ha
// accedido a ellos (backend/privacy). Va en la ficha tras la tarjeta del registro de viajeros, cuyos datos suprime
// solo la purga diaria a los 3 años.
export default function PrivacyActions({ reservation: r, onChanged }) {
  const privacidad = r.privacy || {};
  const suprimidas = { documentos: privacidad.doc_photos_purged_at, vehiculo: privacidad.vehicle_photos_purged_at };
  // Lo suprimido no se ofrece otra vez salvo que haya llegado algo después (`privacy.pendiente`, que el backend
  // calcula con lo que queda en la reserva): suprimir de nuevo cambia la fecha a la de hoy.
  const nuevas = { documentos: privacidad.pendiente?.documentos === true, vehiculo: privacidad.pendiente?.vehiculo === true };
  const bloqueadas = { documentos: Boolean(suprimidas.documentos && !nuevas.documentos),
                       vehiculo: Boolean(suprimidas.vehiculo && !nuevas.vehiculo) };
  const marcadas = () => ({ documentos: !bloqueadas.documentos, vehiculo: false });
  const [abierto, setAbierto] = useState(false);
  const [cats, setCats] = useState(marcadas);
  const [pendiente, setPendiente] = useState(null); // aviso del 409: la reserva no está cerrada
  const [busy, setBusy] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [accesos, setAccesos] = useState(null); // null: cargando; false: no se ha podido leer
  // Exportar y suprimir se apuntan en el registro: después se vuelve a leer.
  const [lectura, setLectura] = useState(0);

  useEffect(() => {
    let vigente = true;
    setAccesos(null);
    api.get("/admin/privacy/accesses", { params: { reservation_id: r.id, limit: 20 } })
      .then(({ data }) => { if (vigente) setAccesos(Array.isArray(data?.rows) ? data.rows : []); })
      .catch(() => { if (vigente) setAccesos(false); });
    return () => { vigente = false; };
  }, [r.id, lectura]);

  const cambiarAbierto = (v) => {
    setAbierto(v);
    if (v) setCats(marcadas());
    else setPendiente(null);
  };

  const suprimir = async () => {
    const categorias = Object.keys(cats).filter((k) => cats[k] && !bloqueadas[k]);
    if (!categorias.length) {
      toast.error("Elige qué fotos suprimir");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/reservations/${r.id}/privacy/purge`, { categorias, confirm: Boolean(pendiente) });
      toast.success(`Fotos suprimidas (${cuantos(data.ficheros || 0, "fichero")})`);
      cambiarAbierto(false);
      setLectura((n) => n + 1);
      onChanged?.();
    } catch (err) {
      if (err.response?.status === 409 && !pendiente) {
        setPendiente(err.response.data?.detail ? formatError(err) : "Confirma para suprimirlas igualmente");
      }
      else toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const exportar = async () => {
    setExportando(true);
    try {
      const resp = await api.get("/admin/privacy/export", { params: { reservation_id: r.id }, responseType: "blob" });
      descargarBlob(resp, `datos-${r.code}.zip`);
      setLectura((n) => n + 1);
    } catch (err) {
      toast.error((await errorDeBlob(err)) || formatError(err));
    } finally {
      setExportando(false);
    }
  };

  return (
    <div className="bg-white border border-[#DCD8D0] rounded-xl p-5 mb-6" data-testid="privacy-actions">
      <h3 className="font-display text-lg font-semibold text-[#1A1A1A] mb-2">Datos personales</h3>
      <ul className="text-sm text-[#5C5C5C] space-y-1 mb-4">
        <li data-testid="privacy-doc-status">Fotos de documentos y ubicación: {estado(suprimidas.documentos, nuevas.documentos)}</li>
        <li data-testid="privacy-vehicle-status">Fotos del vehículo: {estado(suprimidas.vehiculo, nuevas.vehiculo)}</li>
        {privacidad.registry_purged_at && (
          <li data-testid="privacy-registry-status">Datos del registro de viajeros: suprimidos el {fecha(privacidad.registry_purged_at)}</li>
        )}
      </ul>
      <div className="flex flex-wrap gap-2">
        <AlertDialog open={abierto} onOpenChange={cambiarAbierto}>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="border-[#DCD8D0]" disabled={bloqueadas.documentos && bloqueadas.vehiculo}
                    data-testid="privacy-purge-btn">
              <ShieldOff className="h-4 w-4 mr-2" /> Suprimir fotos…
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Suprimir fotos de esta reserva</AlertDialogTitle>
              <AlertDialogDescription>
                Se borran del servidor y no se pueden recuperar. Las firmas y los datos del contrato se conservan.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-2 text-sm text-[#1A1A1A]">
              {CATEGORIAS.map(([cat, texto]) => (
                <label key={cat} className={`flex items-start gap-2 ${bloqueadas[cat] ? "text-[#8A8A8A]" : "cursor-pointer"}`}>
                  <Checkbox checked={Boolean(cats[cat] && !bloqueadas[cat])} disabled={bloqueadas[cat]}
                            onCheckedChange={(v) => setCats((c) => ({ ...c, [cat]: Boolean(v) }))} data-testid={`privacy-cat-${cat}`} />
                  <span>
                    {texto}
                    {bloqueadas[cat] && ` (ya suprimidas el ${fecha(suprimidas[cat])})`}
                    {suprimidas[cat] && nuevas[cat] && ` (se suprimieron el ${fecha(suprimidas[cat])}; quedan las que han llegado después)`}
                  </span>
                </label>
              ))}
              {pendiente && <p role="alert" className="text-[#B54434]" data-testid="privacy-purge-warning">{pendiente}</p>}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <Button onClick={suprimir} disabled={busy} className="bg-[#B54434] hover:bg-[#9C3A2C] text-white" data-testid="privacy-purge-confirm">
                {pendiente ? "Suprimir igualmente" : "Suprimir"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <Button variant="outline" onClick={exportar} disabled={exportando} className="border-[#DCD8D0]" data-testid="privacy-export-btn">
          <Download className="h-4 w-4 mr-2" />{exportando ? "Preparando…" : "Exportar datos del cliente"}
        </Button>
      </div>
      <h4 className="text-sm font-semibold text-[#1A1A1A] mt-5 mb-1">Últimos accesos a estos datos</h4>
      <ul className="text-xs text-[#5C5C5C] space-y-0.5" data-testid="privacy-accesses">
        {accesos === null && <li>Cargando…</li>}
        {accesos === false && <li>No se ha podido leer el registro de accesos</li>}
        {Array.isArray(accesos) && !accesos.length && <li>Sin accesos apuntados</li>}
        {Array.isArray(accesos) && accesos.map((f, i) => <li key={`${f.at}-${i}`}>{lineaAcceso(f)}</li>)}
      </ul>
    </div>
  );
}
